import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, isPaywall } from "@/lib/api";
import { PaywallNotice } from "@/components/shared";
import { toast } from "sonner";
import {
  Scale, Gavel, ShieldCheck, ListChecks, FileSearch, Phone, Clock,
  Sparkles, ChevronDown, Info, Share2, MessageCircle, Facebook, Send, Link2, ImageDown,
} from "lucide-react";

// ---- Story image (Instagram) generated client-side ----
export function downloadStoryImage(title, summary) {
  const c = document.createElement("canvas");
  c.width = 1080; c.height = 1920;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#050505"; ctx.fillRect(0, 0, 1080, 1920);
  const grad = ctx.createRadialGradient(300, 350, 60, 300, 350, 900);
  grad.addColorStop(0, "rgba(212,175,55,0.22)"); grad.addColorStop(1, "rgba(5,5,5,0)");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, 1080, 1920);
  // logo badge
  ctx.fillStyle = "#D4AF37"; ctx.beginPath(); ctx.roundRect(90, 150, 96, 96, 22); ctx.fill();
  ctx.fillStyle = "#050505"; ctx.font = "bold 64px Georgia"; ctx.fillText("B", 118, 218);
  ctx.fillStyle = "#ffffff"; ctx.font = "600 44px Helvetica";
  ctx.fillText("BIZARRE AI", 210, 215);
  ctx.fillStyle = "#D4AF37"; ctx.font = "500 30px Helvetica";
  ctx.fillText("CONNAÎTRE VOS DROITS", 92, 330);
  // title (wrapped)
  ctx.fillStyle = "#ffffff"; ctx.font = "700 82px Georgia";
  const wrap = (text, x, y, maxW, lh, max) => {
    const words = text.split(" "); let line = "", yy = y, n = 0;
    for (const w of words) {
      const test = line + w + " ";
      if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line.trim(), x, yy); line = w + " "; yy += lh; n++; if (n >= max) return yy; }
      else line = test;
    }
    ctx.fillText(line.trim(), x, yy); return yy + lh;
  };
  let y = wrap(title, 92, 560, 900, 96, 4);
  ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.font = "400 40px Helvetica";
  wrap(summary, 92, y + 60, 900, 56, 8);
  // footer
  ctx.fillStyle = "#D4AF37"; ctx.font = "600 38px Helvetica";
  ctx.fillText("Connais tes droits →", 92, 1780);
  ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.font = "400 30px Helvetica";
  ctx.fillText("Information juridique générale · ne remplace pas un avocat", 92, 1840);
  const url = c.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url; a.download = `bizarre-ai-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
  a.click();
}

export function ShareBar({ slug, title, summary }) {
  const shareUrl = `${window.location.origin}/f/${slug}`;
  const text = `${title} — Connais tes droits sur BIZARRE AI`;
  const enc = encodeURIComponent;
  const open = (u) => window.open(u, "_blank", "noopener");

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text, url: shareUrl }); } catch {}
    } else { open(`https://wa.me/?text=${enc(text + " " + shareUrl)}`); }
  };
  const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Lien copié !"); };

  const Btn = ({ onClick, icon: Icon, label, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className="flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-3.5 py-2 text-sm text-white/80 hover:border-gold/40 hover:text-white transition-colors">
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="flex flex-wrap gap-2" data-testid="share-bar">
      <Btn testid="share-native" onClick={nativeShare} icon={Share2} label="Partager" />
      <Btn testid="share-whatsapp" onClick={() => open(`https://wa.me/?text=${enc(text + " " + shareUrl)}`)} icon={MessageCircle} label="WhatsApp" />
      <Btn testid="share-messenger" onClick={() => open(`fb-messenger://share/?link=${enc(shareUrl)}`)} icon={Send} label="Messenger" />
      <Btn testid="share-facebook" onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`)} icon={Facebook} label="Facebook" />
      <Btn testid="share-copy" onClick={copy} icon={Link2} label="Copier le lien" />
      <Btn testid="share-story" onClick={() => downloadStoryImage(title, summary)} icon={ImageDown} label="Image Story" />
    </div>
  );
}

function LawItem({ law, publicMode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  const explain = async () => {
    if (publicMode) { navigate("/login"); return; }
    if (explanation) { setOpen(!open); return; }
    setBusy(true); setPaywall(false);
    try {
      const { data } = await api.post("/ai/rights/explain", { text: `${law.ref} : ${law.plain}` });
      setExplanation(data); setOpen(true);
    } catch (e) {
      if (isPaywall(e)) setPaywall(true); else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`law-${(law.ref || "").slice(0, 20)}`}>
      <p className="text-sm text-white font-medium">{law.ref}</p>
      <p className="text-white/60 text-sm mt-1 leading-relaxed">{law.plain}</p>
      <button data-testid="explain-button" onClick={explain} disabled={busy}
        className="mt-3 inline-flex items-center gap-2 text-xs rounded-full border border-gold/30 text-gold px-3 py-1.5 hover:bg-gold/10 transition-colors disabled:opacity-50">
        <Sparkles size={13} /> {busy ? "..." : "Explique-moi simplement"}
        {explanation && <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {paywall && <PaywallNotice />}
      {open && explanation && (
        <div className="mt-3 rounded-lg bg-gold/[0.05] border border-gold/15 p-3 space-y-2" data-testid="explanation-box">
          <p className="text-white/85 text-sm leading-relaxed">{explanation.explanation}</p>
          {explanation.example && (
            <p className="text-white/60 text-sm leading-relaxed"><span className="text-gold">Exemple : </span>{explanation.example}</p>
          )}
        </div>
      )}
    </div>
  );
}

function ListSection({ icon: Icon, title, items, testid }) {
  if (!items || !items.length) return null;
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-gold" strokeWidth={1.5} />
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-white/80 text-sm leading-relaxed flex gap-2"><span className="text-gold/60">—</span> {it}</li>
        ))}
      </ul>
    </div>
  );
}

export function FicheContent({ sheet, publicMode = false }) {
  return (
    <div className="space-y-4">
      {sheet.laws?.length > 0 && (
        <div className="card-surface p-5" data-testid="section-laws">
          <div className="flex items-center gap-2 mb-3">
            <Gavel size={18} className="text-gold" strokeWidth={1.5} />
            <h3 className="font-display text-lg">Ce que dit la loi</h3>
          </div>
          <div className="space-y-3">
            {sheet.laws.map((law, i) => <LawItem key={i} law={law} publicMode={publicMode} />)}
          </div>
        </div>
      )}
      <ListSection icon={Scale} title="Sanctions prévues" items={sheet.sanctions} testid="section-sanctions" />
      <ListSection icon={ShieldCheck} title="Vos droits en tant que victime" items={sheet.victim_rights} testid="section-rights" />
      <ListSection icon={ListChecks} title="Démarches possibles" items={sheet.steps} testid="section-steps" />
      <ListSection icon={FileSearch} title="Preuves à conserver" items={sheet.evidence} testid="section-evidence" />
      <ListSection icon={Clock} title="Délais à respecter" items={sheet.delais} testid="section-delais" />
      {sheet.organizations?.length > 0 && (
        <div className="card-surface p-5" data-testid="section-organizations">
          <div className="flex items-center gap-2 mb-3">
            <Phone size={18} className="text-gold" strokeWidth={1.5} />
            <h3 className="font-display text-lg">Qui contacter</h3>
          </div>
          <div className="space-y-2">
            {sheet.organizations.map((o, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                <span className="text-white/85 text-sm font-medium">{o.name}</span>
                <span className="text-gold text-sm">{o.contact}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <Info size={18} className="text-gold shrink-0 mt-0.5" />
        <p className="text-sm text-white/55 leading-relaxed">{sheet.disclaimer}</p>
      </div>
    </div>
  );
}
